import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/constants";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Sierra" },
  { to: "/services", label: "Services" },
  { to: "/inservices-training", label: "Inservices" },
  { to: "/knowledge-center", label: "Knowledge Center" },
  { to: "/contact", label: "Contact" },
];

const PRACTICE_AREAS = [
  { to: "/medicaid-ar-recovery", label: "Medicaid & AR Recovery" },
  { to: "/survey-readiness", label: "Survey Readiness" },
  { to: "/operational-risk", label: "Operational Risk" },
  { to: "/litigation-support", label: "Litigation Support" },
  { to: "/speaking-training", label: "Speaking & Training" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ivory/85 backdrop-blur-xl border-b border-champagne-200"
          : "bg-ivory/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-luxe flex items-center justify-between h-20 lg:h-24">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src={BRAND.logoUrl}
            alt="AthenaCrest Defense"
            width="56"
            height="56"
            className="h-12 lg:h-14 w-auto transition-transform group-hover:scale-[1.03]"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-xl lg:text-2xl text-navy tracking-tight">
              AthenaCrest
            </span>
            <span className="font-sans text-[10px] lg:text-xs tracking-overline uppercase text-gold mt-0.5">
              Defense
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-8" data-testid="desktop-nav">
          {PRIMARY.slice(0, 4).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "text-navy after:absolute after:-bottom-1 after:left-0 after:w-full after:h-px after:bg-gold" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              className="nav-link inline-flex items-center gap-1 outline-none"
              data-testid="nav-practice-areas"
            >
              Practice Areas <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-ivory border border-champagne-200 rounded-none mt-4 py-2 min-w-[260px]"
            >
              {PRACTICE_AREAS.map((p) => (
                <DropdownMenuItem
                  key={p.to}
                  asChild
                  className="rounded-none focus:bg-champagne-100 cursor-pointer"
                >
                  <Link
                    to={p.to}
                    data-testid={`nav-practice-${p.to}`}
                    className="font-sans text-sm text-navy py-2 px-4 w-full block hover:text-teal"
                  >
                    {p.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {PRIMARY.slice(4).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "text-navy after:absolute after:-bottom-1 after:left-0 after:w-full after:h-px after:bg-gold" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href={BRAND.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary group"
            data-testid="header-cta-book"
          >
            <span>Book Consultation</span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold group-hover:bg-ivory transition-colors" />
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 text-navy"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-ivory border-t border-champagne-200 animate-fade-in"
          data-testid="mobile-nav"
        >
          <div className="container-luxe py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `font-sans text-base py-3 border-b border-champagne-200 ${
                    isActive ? "text-gold" : "text-navy"
                  }`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={BRAND.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-6 w-full"
              data-testid="mobile-cta-book"
            >
              Book Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
